/**
 * Auth store — Surface Layer (/01)
 *
 * Single Zustand store for the current Identity + error state. Used by
 * the router guard and the LoginPage. The mocked sign-in flow lives in
 * useMockedAuth; this store is transport-agnostic.
 */

import { create } from 'zustand';
import type { AuthError, AuthState, Identity } from '@contracts/identity.contract';
import { emit as emitBridgeEvent } from '@lib/webkit/messageHandler';

const AUTH_STORAGE_KEY = 'surface.auth.identity.v1';

interface AuthStoreApi extends AuthState {
  setLoading: (loading: boolean) => void;
  setError: (error: AuthError | null) => void;
  setIdentity: (identity: Identity | null) => void;
  signOut: () => void;
}

function isIdentity(value: unknown): value is Identity {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<Identity>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.email === 'string' &&
    typeof candidate.displayLabel === 'string' &&
    typeof candidate.tenantId === 'string' &&
    Array.isArray(candidate.roles) &&
    (candidate.defaultPlane === 'control' || candidate.defaultPlane === 'tenant')
  );
}

function readStoredIdentity(): Identity | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return isIdentity(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredIdentity(identity: Identity | null): void {
  if (typeof window === 'undefined') return;
  try {
    if (identity) {
      window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(identity));
    } else {
      window.localStorage.removeItem(AUTH_STORAGE_KEY);
    }
  } catch {
    // Local storage is a UX cache only. Auth remains owned by the backend contract.
  }
}

export const useAuthStore = create<AuthStoreApi>((set) => ({
  identity: readStoredIdentity(),
  isLoading: false,
  error: null,
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setIdentity: (identity) => {
    writeStoredIdentity(identity);
    set({ identity, error: null });
    emitBridgeEvent({
      type: 'AUTH_STATE',
      payload: { authenticated: identity !== null, tenantId: identity?.tenantId },
    });
  },
  signOut: () => {
    writeStoredIdentity(null);
    set({ identity: null, error: null });
    emitBridgeEvent({ type: 'AUTH_STATE', payload: { authenticated: false } });
  },
}));
